import axios from "axios";
import {
  Configuration,
  InternalV1Api,
  PublicV1Api
} from "@bonliva-crm/timereport-api-client-js";
import { environment } from "../../environments/environment";
import { getToken, msalInstance, loginRequest } from "../msalConfig";

/**
 * One authenticated axios instance for every timereport-api call this app makes.
 *
 * It used to be private to the internal API, and the public API was built on a bare axios with no
 * Authorization header. That worked only because GET /api/public/v1/storage/sas/uri was anonymous
 * — which is the defect CRMDEV-7327 closes, so the upload SAS now needs the same bearer token as
 * the rest.
 */
const createAuthenticatedAxios = () => {
  const axiosInstance = axios.create();

  axiosInstance.interceptors.request.use(async (config) => {
    const token = await getToken();

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // One retry with a fresh token. A second 401 on the retried request is an answer, not a
      // reason to ask again — retrying it without a limit kept the call pending for good.
      if (error.response?.status === 401) {
        const newToken = error.config?.authRetried ? "" : await getToken();
        if (newToken) {
          error.config.authRetried = true;
          error.config.headers["Authorization"] = `Bearer ${newToken}`;
          return axiosInstance.request(error.config);
        }

        // No token, or the fresh one was refused too: only signing in again can help.
        msalInstance
          .loginRedirect({
            scopes: loginRequest.scopes
          })
          .catch((err) => {
            if (err?.errorCode !== "interaction_in_progress") {
              console.error("Login redirect error:", err);
            }
          });
      }

      // The caller always hears about the failure (CRMDEV-7588). A promise that never settled
      // left a day save looking saved, so the week could be approved without those hours.
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

// Exported for the receipt download, which needs the raw response — its file name and
// content type are decided by the service, not by anything the caller already holds — and
// so cannot go through a generated operation that types the body away as `void`.
export const authenticatedAxios = createAuthenticatedAxios();

const configuration = new Configuration({
  basePath: environment.timereportServiceUrl
});

export const timeReportService = new InternalV1Api(configuration, null, authenticatedAxios);

export const timeReportServicePublic = new PublicV1Api(configuration, null, authenticatedAxios);

export * from "@bonliva-crm/timereport-api-client-js";
